import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { StatCard } from "@/components/crm/StatCard";
import { statusLabel, APPOINTMENT_STATUS, formatCurrency } from "@/lib/constants";
import { WIDGET_CATALOG, DEFAULT_WIDGET_LAYOUT } from "@/lib/dashboardWidgets";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MultiSelect } from "@/components/ui/multi-select";
import ReportPeriodFilter from "@/components/crm/ReportPeriodFilter";
import { presetRange } from "@/lib/reportPeriods";
import {
  Settings2, X, Plus, GripVertical, Maximize2, Building2,
  CalendarCheck, UserCheck, Percent, IndianRupee,
} from "lucide-react";

// Radix Select can't use an empty-string value, so "All Branches" uses this sentinel.
const ALL_BRANCHES = "all";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend, LabelList,
} from "recharts";

// Shared style for the count labels drawn directly on top of each bar.
const BAR_LABEL = { position: "top", fontSize: 11, fill: "#374151", fontWeight: 600 };
// Adds ~12% headroom above the tallest bar so the "top" count label isn't
// clipped at the chart's upper edge.
const Y_DOMAIN = [0, (max) => Math.ceil((max || 0) * 1.12) || 1];

const PIE_COLORS = ["#1B3A5C", "#C49A6C", "#5B7B9A", "#D9BE9E", "#8FA8BE", "#A98256", "#2F567D", "#E0CDB6"];

const STATUS_BAR_COLORS = {
  new_lead: "#1B3A5C", rnr: "#4CAF50", call_back: "#1565C0",
  appointment_booked: "#9C27B0", interested: "#00897B",
  number_busy: "#F57C00", switched_off: "#EF5350", visited: "#43A047",
  not_interested: "#42A5F5", wrong_number: "#FFA726",
  invalid_number: "#78909C", location_constraint: "#26A69A",
  not_connected: "#AB47BC", not_set: "#BDBDBD", need_more_info: "#EC407A",
};

// value → label / hex colour for appointment-status charts (derived from the
// shared APPOINTMENT_STATUS list so labels stay in sync with the rest of the app).
const APPT_STATUS_LABEL = Object.fromEntries(APPOINTMENT_STATUS.map((s) => [s.value, s.label]));
const APPT_STATUS_COLORS = {
  scheduled: "#1565C0", confirmed: "#00897B", checked_in: "#F57C00",
  in_consultation: "#3949AB", checked_out: "#0891B2", completed: "#0891B2",
  rescheduled: "#FBC02D", cancelled: "#E53935", visited: "#43A047", no_show: "#9E9E9E",
};
const apptStatusLabel = (v) => APPT_STATUS_LABEL[v] || statusLabel(v);
// Compact trend-axis ticks: "2026-07-11" → "11 Jul". The trend charts switch to MONTH
// buckets past ~2 months (This Year → 12 points), so "2026-07" → "Jul '26" — without
// this it fell through as a raw "2026-07", since Date can't parse a bare year-month.
const shortDate = (iso) => {
  const s = String(iso);
  if (/^\d{4}-\d{2}$/.test(s)) {
    const d = new Date(`${s}-01T00:00:00`);
    return Number.isNaN(d.getTime()) ? s
      : `${d.toLocaleDateString("en-IN", { month: "short" })} '${s.slice(2, 4)}`;
  }
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? s
    : d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

// Compact ₹ in Indian units — a revenue axis tick / bar label has to sit next to
// two-digit counts, so "₹4.5L" reads where "₹4,50,000" would collide.
const compactINR = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(v >= 1e8 ? 0 : 1)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(v >= 1e6 ? 0 : 1)}L`;
  if (v >= 1e3) return `₹${Math.round(v / 1e3)}K`;
  return `₹${v}`;
};

const fmtSecs = (s) => {
  if (s == null) return "---";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, "0")}s`;
  return `${sec}s`;
};

const ROLE_LABELS = {
  super_admin: "Super Admin",
  hq_admin: "HQ Admin",
  branch_manager: "Branch Manager",
  franchisee_owner: "Franchisee Owner",
  telecaller: "Tele-caller",
  doctor: "Doctor / Consultant",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [widgets, setWidgets] = useState(DEFAULT_WIDGET_LAYOUT);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(ALL_BRANCHES);
  const [loading, setLoading] = useState(false);
  // Period — defaults to the current IST month, matching the backend's own default.
  const [range, setRange] = useState(() => presetRange("this_month"));
  const [activePreset, setActivePreset] = useState("this_month");
  // Lead-level filters (sent comma-joined; the backend splits them)
  const [fOwners, setFOwners] = useState([]);
  const [fSources, setFSources] = useState([]);
  const [fStatuses, setFStatuses] = useState([]);
  const [telecallers, setTelecallers] = useState([]);
  const [leadOpts, setLeadOpts] = useState({ sources: [], statuses: [] });
  // The Call Activity table owns its own window — it opens on Today (the way it's read
  // day-to-day) and can pull the selected period without moving the picker for every
  // other card on the page.
  const [callScope, setCallScope] = useState("today");
  const [myPerf, setMyPerf] = useState(null);

  // Only roles that see across branches get the picker; others are already scoped.
  // The same role set may filter by owner — for a tele-caller the backend ignores an
  // assigned_to param (it would otherwise widen their own scope), so no control here.
  const canPickBranch = ["super_admin", "hq_admin", "branch_manager", "franchisee_owner"].includes(user?.role);

  // Load the branch list (narrowed to the user's own branches for a branch manager).
  useEffect(() => {
    if (!canPickBranch) return;
    api.get("/branches").then((r) => {
      let list = r.data || [];
      // Narrow the dropdown to the branches this role may actually pick (the backend
      // 403s on out-of-scope picks; this keeps the options from ever showing them).
      if (user?.role === "branch_manager" && (user?.branch_ids || []).length) {
        list = list.filter((b) => (user.branch_ids || []).includes(b.id));
      } else if (user?.role === "franchisee_owner" && user?.franchise_id) {
        list = list.filter((b) => b.franchise_id === user.franchise_id);
      }
      setBranches(list);
    }).catch(() => {});
  }, [user, canPickBranch]);

  // Filter dropdown values: tele-callers for the owner filter, master-data vocab for
  // source/status (same endpoints the Leads filters use).
  useEffect(() => {
    api.get("/leads/options")
      .then((r) => setLeadOpts({ sources: r.data?.sources || [], statuses: r.data?.statuses || [] }))
      .catch(() => {});
    if (!canPickBranch) return;
    api.get("/users", { params: { role: "telecaller" } })
      .then((r) => setTelecallers(r.data || []))
      .catch(() => {});
  }, [canPickBranch]);

  // Join the array filters into stable strings so they can be effect deps directly.
  const ownersKey = fOwners.join(",");
  const sourcesKey = fSources.join(",");
  const statusesKey = fStatuses.join(",");

  // Stats re-fetch whenever the period, branch or any filter changes.
  useEffect(() => {
    const params = { from: range.from, to: range.to };
    if (selectedBranch && selectedBranch !== ALL_BRANCHES) params.branch_id = selectedBranch;
    if (ownersKey) params.assigned_to = ownersKey;
    if (sourcesKey) params.source = sourcesKey;
    if (statusesKey) params.status = statusesKey;
    params.call_scope = callScope;
    setLoading(true);
    api.get("/dashboard", { params })
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedBranch, range.from, range.to, ownersKey, sourcesKey, statusesKey, callScope]);

  // Own booking scoreboard. Tele-callers only: it answers "what did I book, and
  // did it show up", which no other role is measured on. Follows the same period
  // as the rest of the page.
  useEffect(() => {
    if (user?.role !== "telecaller") { setMyPerf(null); return; }
    api.get("/reports/my-performance", { params: { from: range.from, to: range.to } })
      .then((res) => setMyPerf(res.data))
      .catch(() => setMyPerf(null));
  }, [user?.role, range.from, range.to]);

  // Layout prefs are per-user, not branch-scoped — fetch once.
  useEffect(() => {
    api.get("/dashboard/widgets").then((res) => setWidgets(res.data.widgets)).catch(() => {});
  }, []);

  const saveLayout = useCallback((next) => {
    setWidgets(next);
    api.put("/dashboard/widgets", { widgets: next }).catch(() => toast.error("Could not save dashboard layout"));
  }, []);

  const applyPreset = (key) => {
    setActivePreset(key);
    setRange(presetRange(key));
  };
  const setCustom = (field, value) => {
    if (!value) return;
    setActivePreset(null);
    setRange((r) => ({ ...r, [field]: value }));
  };

  if (!data) {
    return (
      <div className="grid gap-6 md:grid-cols-4">
        {[...Array(8)].map((_, i) => <div key={i} className="h-28 animate-pulse rounded-xl bg-white/60" />)}
      </div>
    );
  }

  const k = data.kpis;
  const show = data.show || {};
  const role = user?.role;

  // Label the window the SERVER actually applied (data.period), not local state, so
  // the caption can never disagree with the numbers under it.
  const fmtDay = (iso) =>
    new Date(`${iso}T12:00:00`).toLocaleDateString("en-IN", {
      day: "numeric", month: "short", year: "numeric",
    });
  const periodLabel = data.period
    ? `${fmtDay(data.period.from)} – ${fmtDay(data.period.to)}`
    : `${range.from} – ${range.to}`;

  const funnelData = (data.funnel || [])
    .filter((f) => !["not_interested", "wrong_number", "invalid_number", "location_constraint"].includes(f.stage))
    .map((f) => ({ name: statusLabel(f.stage), count: f.count }));

  const visibleWidgets = widgets.filter((key) => WIDGET_CATALOG[key]?.visible(show));
  const availableToAdd = Object.keys(WIDGET_CATALOG).filter(
    (key) => WIDGET_CATALOG[key].visible(show) && !widgets.includes(key)
  );

  const removeWidget = (key) => saveLayout(widgets.filter((w) => w !== key));
  const addWidget = (key) => saveLayout([...widgets, key]);

  return (
    <div className="space-y-8">
      {/* Role banner — only shown for non-admin roles so they know data is scoped */}
      {role && !["super_admin", "hq_admin"].includes(role) && (
        <div className="rounded-xl border border-navy/10 bg-navy/5 px-5 py-3 text-sm text-navy">
          Showing data scoped to your {role === "telecaller" ? "assigned leads, follow-ups & the appointments you booked" : role === "doctor" ? "appointments" : role === "branch_manager" ? "branch" : "franchise"}.
        </div>
      )}

      {/* Period picker — the same control and IST presets the Reports page uses, so a
          given range means the same thing on both pages. */}
      <ReportPeriodFilter
        range={range}
        activePreset={activePreset}
        onPreset={applyPreset}
        onCustom={setCustom}
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {canPickBranch && branches.length > 0 && (
            <>
              <Building2 className="h-4 w-4 text-navy/60" />
              <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                <SelectTrigger className="h-8 w-56 border-navy/20 text-sm" data-testid="dashboard-branch-select">
                  <SelectValue placeholder="All Branches" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_BRANCHES}>All Branches</SelectItem>
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </>
          )}
          {canPickBranch && telecallers.length > 0 && (
            <MultiSelect
              className="h-8 w-44 text-sm"
              data-testid="dashboard-filter-owner"
              placeholder="All Tele-callers"
              options={telecallers.map((t) => ({ value: t.id, label: t.name }))}
              selected={fOwners}
              onChange={setFOwners}
            />
          )}
          {(leadOpts.sources || []).length > 0 && (
            <MultiSelect
              className="h-8 w-40 text-sm"
              data-testid="dashboard-filter-source"
              placeholder="All Sources"
              options={leadOpts.sources.map((s) => ({ value: s, label: s }))}
              selected={fSources}
              onChange={setFSources}
            />
          )}
          {(leadOpts.statuses || []).length > 0 && (
            <MultiSelect
              className="h-8 w-40 text-sm"
              data-testid="dashboard-filter-status"
              placeholder="All Statuses"
              options={leadOpts.statuses.map((s) => ({ value: s, label: statusLabel(s) }))}
              selected={fStatuses}
              onChange={setFStatuses}
            />
          )}
          {loading && <span className="text-xs text-navy/50">Loading…</span>}
        </div>
        <Button variant="outline" size="sm" onClick={() => setCustomizeOpen(true)} className="border-navy/20 text-navy hover:bg-navy/5" data-testid="customize-dashboard-btn">
          <Settings2 className="mr-1.5 h-4 w-4" /> Customize Dashboard
        </Button>
      </div>

      {/* Spell out the scope: most tiles cover the period, a few deliberately don't. */}
      <p className="-mb-4 text-xs text-navy/60" data-testid="dashboard-scope-note">
        Showing <span className="font-medium text-navy">{periodLabel}</span>. Tiles
        labelled Today / This Month and the live counts (Hot Leads, Active Patients,
        Missed Follow-ups) ignore this range. Call Activity opens on Today and has its
        own Today / Selected period switch.
      </p>

      {/* My Performance — tele-caller's own bookings for the period. Cohorted by
          when the appointment was BOOKED, so "Visited" is the outcome of this
          period's bookings, not visits that happened in it. */}
      {myPerf && (
        <section data-testid="my-performance">
          <h2 className="mb-3 font-display text-lg font-semibold text-navy">
            My Performance
            <span className="ml-2 text-xs font-normal text-navy/50">
              appointments I booked in {periodLabel}
            </span>
          </h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              icon={CalendarCheck}
              label="Appointments Booked"
              value={myPerf.booked ?? 0}
              sub={myPerf.pending ? `${myPerf.pending} still upcoming` : null}
              testId="myperf-booked"
            />
            <StatCard
              icon={UserCheck}
              label="Visited"
              value={myPerf.visited ?? 0}
              sub={myPerf.no_show ? `${myPerf.no_show} no-show` : null}
              testId="myperf-visited"
            />
            <StatCard
              icon={Percent}
              label="Show Rate"
              value={`${myPerf.show_rate ?? 0}%`}
              sub="of bookings that reached their date"
              testId="myperf-show-rate"
            />
            <StatCard
              icon={IndianRupee}
              label="Revenue"
              value={formatCurrency(myPerf.revenue_total ?? 0)}
              sub={`${formatCurrency(myPerf.revenue_collected ?? 0)} collected · ${formatCurrency(myPerf.revenue_outstanding ?? 0)} due`}
              accent="gold"
              testId="myperf-revenue"
            />
          </div>
        </section>
      )}

      {/* KPI grid */}
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {visibleWidgets.filter((key) => WIDGET_CATALOG[key]?.type === "kpi").map((key) => {
          const w = WIDGET_CATALOG[key];
          const built = w.build(k, show);
          const Icon = built.icon || w.icon;
          return (
            <StatCard
              key={key}
              icon={Icon}
              label={built.label || w.label}
              value={built.value}
              accent={w.accent}
              testId={`kpi-${key}`}
            />
          );
        })}
      </div>

      {/* Charts — full-width charts one per row, the rest two per line */}
      <DashboardCharts
        data={data}
        funnelData={funnelData}
        visibleWidgets={visibleWidgets}
        ctx={{ periodLabel, setCallScope }}
      />

      {/* Doctor / limited role — appointment-focused empty state for charts */}
      {!show.funnel && !show.branch_performance && (
        <div className="rounded-xl border border-navy/10 bg-white p-8 text-center text-sm text-muted-foreground shadow-sm">
          Pipeline and branch charts are available to managers and above.
        </div>
      )}

      <CustomizeDialog
        open={customizeOpen}
        onOpenChange={setCustomizeOpen}
        widgets={widgets}
        availableToAdd={availableToAdd}
        onRemove={removeWidget}
        onAdd={addWidget}
        onReorder={saveLayout}
      />
    </div>
  );
}

const CHART_SHELL = "rounded-xl border border-navy/10 bg-white p-6 shadow-sm";
const TT_STYLE = { borderRadius: 12, border: "1px solid #1B3A5C20" };

// "Calls By Hour" heatmap — hours 8 AM … 5 PM (IST), matching backend CALL_HEATMAP_HOURS.
const CALL_HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17];
const hourLabel = (h) =>
  h === 12 ? "12 PM" : h < 12 ? `${h} AM` : `${h - 12} PM`;
// Shade a cell from light to deep blue based on its share of the row's busiest hour.
const heatCellStyle = (count, max) => {
  if (!count) return { background: "#fff", color: "#cbd5e1" };
  const t = max > 0 ? count / max : 0;          // 0 … 1 within this user's row
  const alpha = 0.18 + t * 0.82;                 // never fully transparent
  return {
    background: `rgba(37, 99, 235, ${alpha.toFixed(2)})`, // blue-600
    color: t > 0.5 ? "#fff" : "#1e3a8a",
  };
};

/**
 * Small segmented control for a card that scopes itself independently of the page
 * period picker (currently just Call Activity).
 */
function ScopeToggle({ value, onChange, options }) {
  return (
    <div className="inline-flex shrink-0 rounded-lg border border-navy/15 bg-cream/50 p-0.5" data-testid="scope-toggle">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          aria-pressed={value === o.value}
          className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
            value === o.value ? "bg-navy text-white shadow-sm" : "text-navy/70 hover:text-navy"
          }`}
          data-testid={`scope-opt-${o.value}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Card shell shared by every dashboard chart. Renders the title/subtitle, an
 * expand button (top-right), the chart body, and a full-screen Dialog showing
 * the same content enlarged. `body` is a render function (height) => node so the
 * dialog can render a taller version of the chart. `action` is an optional control
 * (e.g. a scope toggle) shown beside the title, and repeated in the dialog header.
 */
function ChartFrame({ title, subtitle, action, body }) {
  const [full, setFull] = useState(false);
  return (
    <>
      <div className={`${CHART_SHELL} relative flex h-full flex-col`}>
        <button
          onClick={() => setFull(true)}
          className="absolute right-4 top-4 rounded-md p-1.5 text-muted-foreground hover:bg-cream hover:text-navy"
          title="View full screen"
          aria-label="View full screen"
        >
          <Maximize2 className="h-4 w-4" />
        </button>
        {/* pr-10 keeps the header clear of the absolutely-placed expand button */}
        <div className="flex flex-wrap items-start justify-between gap-3 pr-10">
          <div>
            <h3 className="font-display text-lg font-semibold text-navy">{title}</h3>
            {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
          </div>
          {action}
        </div>
        <div className="mt-4 flex-1">{body(300)}</div>
      </div>

      <Dialog open={full} onOpenChange={setFull}>
        <DialogContent className="max-w-[95vw] w-full max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">{title}</DialogTitle>
            {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
          </DialogHeader>
          {action && <div className="mt-1">{action}</div>}
          <div className="mt-2">{body(Math.round(window.innerHeight * 0.7))}</div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Returns the chart descriptor for a given key, or null if it has no data.
// `ctx` carries what a card needs beyond `data`: the period caption and the
// Call Activity scope state.
function chartDescriptor(chartKey, data, funnelData, ctx) {
  switch (chartKey) {
    case "funnel_chart":
      return {
        title: "Conversion Funnel",
        subtitle: "Leads by pipeline stage",
        body: (h) => (
          <ResponsiveContainer width="100%" height={h}>
            <BarChart data={funnelData} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5ddd2" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6B7280" }} angle={-25} textAnchor="end" height={70} interval={0} />
              <YAxis tick={{ fontSize: 12, fill: "#6B7280" }} allowDecimals={false} domain={Y_DOMAIN} />
              <Tooltip contentStyle={TT_STYLE} />
              <Bar dataKey="count" fill="#1B3A5C" radius={[6, 6, 0, 0]}>
                <LabelList dataKey="count" {...BAR_LABEL} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ),
      };
    case "sources_chart":
      if (!(data.sources || []).length) return null;
      return {
        title: "Lead Sources",
        subtitle: "Acquisition channels",
        body: (h) => (
          <ResponsiveContainer width="100%" height={h}>
            <PieChart>
              <Pie data={data.sources} dataKey="leads" nameKey="source" cx="50%" cy="45%" outerRadius={85} innerRadius={45} paddingAngle={2}
                label={({ leads }) => leads} labelLine={false} fontSize={11}>
                {data.sources.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={TT_STYLE} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        ),
      };
    case "branch_performance":
      if (!(data.branch_performance || []).length) return null;
      return {
        title: "Branch Performance",
        subtitle: "Leads vs. conversions by clinic",
        body: (h) => (
          <ResponsiveContainer width="100%" height={h}>
            <BarChart data={data.branch_performance} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5ddd2" vertical={false} />
              <XAxis dataKey="branch" tick={{ fontSize: 12, fill: "#6B7280" }} />
              <YAxis tick={{ fontSize: 12, fill: "#6B7280" }} allowDecimals={false} domain={Y_DOMAIN} />
              <Tooltip contentStyle={TT_STYLE} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="leads" name="Leads" fill="#1B3A5C" radius={[6, 6, 0, 0]}>
                <LabelList dataKey="leads" {...BAR_LABEL} />
              </Bar>
              <Bar dataKey="converted" name="Converted" fill="#C49A6C" radius={[6, 6, 0, 0]}>
                <LabelList dataKey="converted" {...BAR_LABEL} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ),
      };
    case "branch_overview": {
      const rows = data.branch_overview || [];
      if (!rows.length) return null;
      // Revenue is admin-only (the backend omits the key otherwise) and lives on a
      // second axis — on a shared scale a ₹-in-lakhs bar would flatten the counts
      // next to it into an invisible line.
      const withRevenue = !!(data.show || {}).revenue;
      return {
        title: "Branch Overview",
        subtitle: withRevenue
          ? "Leads, appointments booked, visited & revenue per branch"
          : "Leads, appointments booked & visited per branch",
        body: (h) => (
          <ResponsiveContainer width="100%" height={h}>
            <BarChart data={rows} margin={{ left: -10, right: withRevenue ? 8 : 0 }} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5ddd2" vertical={false} />
              <XAxis dataKey="branch" tick={{ fontSize: 11, fill: "#6B7280" }} angle={-20} textAnchor="end" height={70} interval={0} />
              <YAxis yAxisId="count" tick={{ fontSize: 12, fill: "#6B7280" }} allowDecimals={false} domain={Y_DOMAIN} />
              {withRevenue && (
                <YAxis yAxisId="revenue" orientation="right" width={62} domain={Y_DOMAIN}
                  tick={{ fontSize: 11, fill: "#A98256" }} tickFormatter={compactINR} />
              )}
              <Tooltip
                contentStyle={TT_STYLE}
                formatter={(value, name) => [name === "Revenue" ? formatCurrency(value) : value, name]}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="count" dataKey="leads" name="Leads" fill="#1B3A5C" radius={[6, 6, 0, 0]}>
                <LabelList dataKey="leads" {...BAR_LABEL} />
              </Bar>
              <Bar yAxisId="count" dataKey="booked" name="Appointments Booked" fill="#5B7B9A" radius={[6, 6, 0, 0]}>
                <LabelList dataKey="booked" {...BAR_LABEL} />
              </Bar>
              <Bar yAxisId="count" dataKey="visited" name="Visited" fill="#43A047" radius={[6, 6, 0, 0]}>
                <LabelList dataKey="visited" {...BAR_LABEL} />
              </Bar>
              {withRevenue && (
                <Bar yAxisId="revenue" dataKey="revenue" name="Revenue" fill="#C49A6C" radius={[6, 6, 0, 0]}>
                  <LabelList dataKey="revenue" {...BAR_LABEL} formatter={compactINR} />
                </Bar>
              )}
            </BarChart>
          </ResponsiveContainer>
        ),
      };
    }
    case "call_activity_table": {
      const callRows = data.call_activity || [];
      // Trust the scope the SERVER applied, so the toggle and the subtitle can never
      // describe a window the numbers below don't come from.
      const callScope = data.call_activity_scope === "period" ? "period" : "today";
      // Deliberately NOT hidden when empty (unlike the other charts): the card owns the
      // only control that can switch it off Today, so vanishing on a quiet morning
      // would strand the user with no way back to the period view.
      return {
        title: "Call Activity Of All Project Users",
        subtitle: callScope === "today"
          ? "Today's call stats per user — times in IST"
          : `Call stats per user for ${ctx.periodLabel} — times in IST`,
        action: (
          <ScopeToggle
            value={callScope}
            onChange={ctx.setCallScope}
            options={[{ value: "today", label: "Today" }, { value: "period", label: "Selected period" }]}
          />
        ),
        body: () => (callRows.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {callScope === "today"
              ? "No calls logged today yet."
              : "No calls logged in the selected period."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full whitespace-nowrap text-sm">
              <thead>
                <tr className="border-b bg-cream/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">User Name</th>
                  <th className="px-4 py-3">Total Calls</th>
                  <th className="px-4 py-3">Connected Calls</th>
                  <th className="px-4 py-3">Outgoing</th>
                  <th className="px-4 py-3">Incoming</th>
                  <th className="px-4 py-3">Total Call Duration(m)</th>
                  <th className="px-4 py-3">Avg Call Duration(m)</th>
                  <th className="px-4 py-3">First Call At</th>
                  <th className="px-4 py-3">Last Call At</th>
                  <th className="px-4 py-3">Calls By Hour</th>
                </tr>
              </thead>
              <tbody>
                {callRows.map((row, i) => {
                  const hours = row.by_hour || [];
                  const max = Math.max(0, ...hours);
                  return (
                    <tr key={i} className="border-b align-middle last:border-0 hover:bg-nude/30">
                      <td className="px-4 py-3 font-semibold text-ink">{row.name}</td>
                      <td className="px-4 py-3 text-muted-foreground">{row.total_calls}</td>
                      <td className="px-4 py-3 text-muted-foreground">{row.connected_calls}</td>
                      <td className="px-4 py-3 text-muted-foreground">{row.outgoing}</td>
                      <td className="px-4 py-3 text-muted-foreground">{row.incoming}</td>
                      <td className="px-4 py-3 text-muted-foreground">{row.total_duration_min}</td>
                      <td className="px-4 py-3 text-muted-foreground">{row.avg_duration_min}</td>
                      <td className="px-4 py-3 text-muted-foreground">{row.first_call_at || "-"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{row.last_call_at || "-"}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          {CALL_HOURS.map((h, hi) => {
                            const count = hours[hi] || 0;
                            return (
                              <div key={h} className="flex flex-col items-center">
                                <span className="mb-1 text-[10px] leading-none text-muted-foreground">{hourLabel(h)}</span>
                                <div
                                  className="flex h-7 w-9 items-center justify-center rounded border border-navy/10 text-xs font-medium"
                                  style={heatCellStyle(count, max)}
                                  title={`${hourLabel(h)}: ${count} call${count === 1 ? "" : "s"}`}
                                >
                                  {count > 0 ? count : ""}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )),
      };
    }
    case "leads_today_by_source":
      if (!(data.leads_today_by_source || []).length) return null;
      return {
        title: "Leads Created Today by Source",
        subtitle: "Count of today's leads grouped by acquisition source",
        body: (h) => (
          <ResponsiveContainer width="100%" height={h}>
            <BarChart data={data.leads_today_by_source} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5ddd2" vertical={false} />
              <XAxis dataKey="source" tick={{ fontSize: 11, fill: "#6B7280" }} angle={-25} textAnchor="end" height={70} interval={0} />
              <YAxis tick={{ fontSize: 12, fill: "#6B7280" }} allowDecimals={false} domain={Y_DOMAIN} />
              <Tooltip contentStyle={TT_STYLE} />
              <Bar dataKey="count" fill="#C49A6C" radius={[6, 6, 0, 0]}>
                <LabelList dataKey="count" {...BAR_LABEL} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ),
      };
    case "leads_by_status":
      if (!(data.leads_by_status || []).length) return null;
      return {
        title: "Leads By Status",
        subtitle: "Distribution across all pipeline statuses",
        body: (h) => (
          <ResponsiveContainer width="100%" height={h}>
            <BarChart data={data.leads_by_status.map((d) => ({ ...d, name: statusLabel(d.status) }))} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5ddd2" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#6B7280" }} angle={-35} textAnchor="end" height={90} interval={0} />
              <YAxis tick={{ fontSize: 12, fill: "#6B7280" }} allowDecimals={false} domain={Y_DOMAIN} />
              <Tooltip contentStyle={TT_STYLE} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                <LabelList dataKey="count" {...BAR_LABEL} />
                {data.leads_by_status.map((entry, i) => (
                  <Cell key={i} fill={STATUS_BAR_COLORS[entry.status] || PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ),
      };
    case "lead_count_by_owner":
      if (!(data.lead_count_by_owner || []).length) return null;
      return {
        title: "Lead Count By Owner",
        subtitle: "Total leads assigned per user",
        body: (h) => (
          <ResponsiveContainer width="100%" height={h}>
            <BarChart data={data.lead_count_by_owner} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5ddd2" vertical={false} />
              <XAxis dataKey="owner" tick={{ fontSize: 11, fill: "#6B7280" }} angle={-25} textAnchor="end" height={70} interval={0} />
              <YAxis tick={{ fontSize: 12, fill: "#6B7280" }} allowDecimals={false} domain={Y_DOMAIN} />
              <Tooltip contentStyle={TT_STYLE} />
              <Bar dataKey="count" fill="#1B3A5C" radius={[6, 6, 0, 0]}>
                <LabelList dataKey="count" {...BAR_LABEL} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ),
      };
    case "leads_by_source_all":
      if (!(data.leads_by_source_all || []).length) return null;
      return {
        title: "Leads By Source",
        subtitle: "All-time lead distribution by acquisition channel",
        body: (h) => (
          <ResponsiveContainer width="100%" height={h}>
            <BarChart data={data.leads_by_source_all} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5ddd2" vertical={false} />
              <XAxis dataKey="source" tick={{ fontSize: 11, fill: "#6B7280" }} angle={-25} textAnchor="end" height={70} interval={0} />
              <YAxis tick={{ fontSize: 12, fill: "#6B7280" }} allowDecimals={false} domain={Y_DOMAIN} />
              <Tooltip contentStyle={TT_STYLE} />
              <Bar dataKey="count" fill="#5B7B9A" radius={[6, 6, 0, 0]}>
                <LabelList dataKey="count" {...BAR_LABEL} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ),
      };
    // Two trends, same shape, different date field — see _appt_trend() in the backend.
    // scheduled_at = when appointments HAPPEN (clinic load); created_at = when they
    // were BOOKED (sales output). Both follow the period picker.
    case "appointments_trend": {
      const rows = (data.appointments_trend || []).map((d) => ({ ...d, name: shortDate(d.date) }));
      if (!rows.length) return null;
      return {
        title: "Appointments Trend",
        subtitle: "Appointments per day, by appointment date",
        body: (h) => (
          <ResponsiveContainer width="100%" height={h}>
            <BarChart data={rows} margin={{ left: -10, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5ddd2" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#6B7280" }} interval="preserveStartEnd" minTickGap={12} />
              <YAxis tick={{ fontSize: 12, fill: "#6B7280" }} allowDecimals={false} domain={Y_DOMAIN} />
              <Tooltip contentStyle={TT_STYLE} />
              <Bar dataKey="count" name="Appointments" fill="#1B3A5C" radius={[6, 6, 0, 0]}>
                <LabelList dataKey="count" {...BAR_LABEL} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ),
      };
    }
    case "appointments_booked_trend": {
      const rows = (data.appointments_booked_trend || []).map((d) => ({ ...d, name: shortDate(d.date) }));
      if (!rows.length) return null;
      return {
        title: "Appointments Booked Trend",
        subtitle: "Bookings made per day, by date created",
        body: (h) => (
          <ResponsiveContainer width="100%" height={h}>
            <BarChart data={rows} margin={{ left: -10, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5ddd2" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#6B7280" }} interval="preserveStartEnd" minTickGap={12} />
              <YAxis tick={{ fontSize: 12, fill: "#6B7280" }} allowDecimals={false} domain={Y_DOMAIN} />
              <Tooltip contentStyle={TT_STYLE} />
              {/* Same blue-grey this measure gets in the Branch Overview chart. */}
              <Bar dataKey="count" name="Booked" fill="#5B7B9A" radius={[6, 6, 0, 0]}>
                <LabelList dataKey="count" {...BAR_LABEL} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ),
      };
    }
    case "appointments_by_status": {
      const rows = (data.appointments_by_status || []).map((d) => ({ ...d, name: apptStatusLabel(d.status) }));
      if (!rows.length) return null;
      return {
        title: "Appointments By Status",
        subtitle: "Show-up vs. cancellation breakdown",
        body: (h) => (
          <ResponsiveContainer width="100%" height={h}>
            <PieChart>
              <Pie data={rows} dataKey="count" nameKey="name" cx="50%" cy="45%" outerRadius={85} innerRadius={45} paddingAngle={2}
                label={({ count }) => count} labelLine={false} fontSize={11}>
                {rows.map((entry, i) => (
                  <Cell key={i} fill={APPT_STATUS_COLORS[entry.status] || PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={TT_STYLE} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        ),
      };
    }
    default:
      return null;
  }
}

// Charts that should always span the full row (wide table / funnel, and the
// 4-bars-per-branch overview, which is unreadable at half width).
const FULL_WIDTH_CHARTS = new Set(["funnel_chart", "call_activity_table", "branch_overview"]);

/**
 * Renders the chart section: full-width charts stay one-per-row; the rest are
 * laid out two-per-line in a responsive grid. Each card gets a full-screen view.
 */
function DashboardCharts({ data, funnelData, visibleWidgets, ctx }) {
  const chartKeys = visibleWidgets.filter((key) => WIDGET_CATALOG[key]?.type === "chart");

  // Build [key, descriptor] pairs, dropping charts with no data.
  const items = chartKeys
    .map((key) => [key, chartDescriptor(key, data, funnelData, ctx)])
    .filter(([, d]) => d);

  // Group consecutive non-full-width charts so we can put two per line.
  const rows = [];
  for (const [key, d] of items) {
    if (FULL_WIDTH_CHARTS.has(key)) {
      rows.push({ full: true, key, d });
    } else {
      const last = rows[rows.length - 1];
      if (last && last.grid) last.items.push({ key, d });
      else rows.push({ grid: true, items: [{ key, d }] });
    }
  }

  return (
    <>
      {rows.map((row, ri) =>
        row.full ? (
          <ChartFrame key={row.key} {...row.d} />
        ) : (
          <div key={`grid-${ri}`} className="grid gap-6 lg:grid-cols-2">
            {row.items.map(({ key, d }) => <ChartFrame key={key} {...d} />)}
          </div>
        )
      )}
    </>
  );
}

function DragList({ items, type, onReorder, onRemove }) {
  const [dragIdx, setDragIdx] = React.useState(null);
  const [overIdx, setOverIdx] = React.useState(null);

  const handleDragStart = (e, idx) => { setDragIdx(idx); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(idx)); };
  const handleDragOver = (e, idx) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (overIdx !== idx) setOverIdx(idx); };
  const handleDrop = (e, dropIdx) => {
    e.preventDefault();
    if (dragIdx !== null && dragIdx !== dropIdx) {
      const next = [...items];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(dropIdx, 0, moved);
      onReorder(next, type);
    }
    setDragIdx(null); setOverIdx(null);
  };
  const handleDragEnd = () => { setDragIdx(null); setOverIdx(null); };

  if (items.length === 0) return <p className="py-3 text-center text-sm text-muted-foreground">None added</p>;

  return (
    <div className="space-y-1.5">
      {items.map((key, i) => {
        const w = WIDGET_CATALOG[key];
        if (!w) return null;
        const isDragging = dragIdx === i;
        const isOver = overIdx === i && dragIdx !== i;
        return (
          <div key={key} draggable onDragStart={(e) => handleDragStart(e, i)} onDragOver={(e) => handleDragOver(e, i)}
            onDrop={(e) => handleDrop(e, i)} onDragEnd={handleDragEnd}
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 transition-all select-none ${
              isDragging ? "opacity-40 border-navy/20 bg-cream/40" : isOver ? "border-navy/40 bg-navy/5 shadow-sm" : "border-navy/10 bg-cream/40"
            }`} style={{ cursor: "grab" }} data-testid={`widget-row-${key}`}
          >
            <GripVertical className="h-4 w-4 text-navy/30 shrink-0" />
            <span className="flex-1 text-sm font-medium text-ink">{w.label}</span>
            <button onClick={() => onRemove(key)} className="rounded p-1 text-red-500 hover:bg-red-50 shrink-0" data-testid={`widget-remove-${key}`}>
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}

function CustomizeDialog({ open, onOpenChange, widgets, availableToAdd, onRemove, onAdd, onReorder }) {
  const stats = widgets.filter((k) => WIDGET_CATALOG[k]?.type === "kpi");
  const charts = widgets.filter((k) => WIDGET_CATALOG[k]?.type === "chart");
  const addableStats = availableToAdd.filter((k) => WIDGET_CATALOG[k]?.type === "kpi");
  const addableCharts = availableToAdd.filter((k) => WIDGET_CATALOG[k]?.type === "chart");

  const handleSectionReorder = (reordered, type) => {
    if (type === "kpi") {
      onReorder([...reordered, ...charts]);
    } else {
      onReorder([...stats, ...reordered]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-navy">Customize Dashboard</DialogTitle></DialogHeader>
        <div className="space-y-5 py-2">
          {/* Stats section */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Stats</p>
            <p className="mb-3 text-xs text-muted-foreground">Drag to reorder</p>
            <DragList items={stats} type="kpi" onReorder={handleSectionReorder} onRemove={onRemove} />
            {addableStats.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {addableStats.map((key) => (
                  <button key={key} onClick={() => onAdd(key)}
                    className="flex items-center gap-1.5 rounded-full border border-navy/20 px-3 py-1.5 text-sm font-medium text-navy hover:bg-navy/5"
                    data-testid={`widget-add-${key}`}
                  >
                    <Plus className="h-3.5 w-3.5" /> {WIDGET_CATALOG[key].label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <hr className="border-navy/10" />

          {/* Charts section */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Charts</p>
            <p className="mb-3 text-xs text-muted-foreground">Drag to reorder</p>
            <DragList items={charts} type="chart" onReorder={handleSectionReorder} onRemove={onRemove} />
            {addableCharts.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {addableCharts.map((key) => (
                  <button key={key} onClick={() => onAdd(key)}
                    className="flex items-center gap-1.5 rounded-full border border-navy/20 px-3 py-1.5 text-sm font-medium text-navy hover:bg-navy/5"
                    data-testid={`widget-add-${key}`}
                  >
                    <Plus className="h-3.5 w-3.5" /> {WIDGET_CATALOG[key].label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} className="bg-navy text-white hover:bg-navy-hover">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
